import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import _ from 'lodash';

import { ProfileContext } from '../contexts/ProfileContext';

import { Popups, Spinner } from '../components';
import GenericForm from '../components/forms/genericForm';
import Loader from '../components/common/Loader';
import { CaretLeftIcon } from '@phosphor-icons/react';

import {
  getProfessions,
  getRegions,
  updateProfileData,
  getFunction,
  getProfessionalAssociation,
  getSpecialJobTitles,
  getVocationalSchool,
  getRegionalEvents,
  getParticipantLocations
} from '../service';

import { getSortedList, getTranslation } from '../utils';
import { genericFormSchema } from '../utils/schema';
import Button from '../components/ui/button';

export default function PgProfileData() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [profile, setProfile] = useContext(ProfileContext);

  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [regionalEvents, setRegionalEvents] = useState([]);
  const [participantLocations, setParticipantLocations] = useState([]);
  const [listSwissRegions, setListSwissRegions] = useState([]);
  const [listProfessionalAssoc, setListProfessionalAssoc] = useState([]);
  const [listSwissFunction, setListSwissFunction] = useState([]);
  const [listSwissJobTitle, setListSwissJobTitle] = useState([]);
  const [swissJobTitleList, setSwissJobTitle] = useState([]);
  const [listVocationalSchool, setListVocationalSchool] = useState([]);
  const [missingFieldsError, setMissingFieldsError] = useState(false);
  const [unsavedChangesError, setUnsavedChangesError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requiredFieldMissing, setRequiredFieldMissing] = useState([]);
  const [initialValues, setInitialValues] = useState({ ...profile });

  const {
    profileTypeData: { editableForm, inputFields: fieldKeys, customData } = {},
    masterData: { translationPrefix, customConfigurations: { colors } = {} } = {}
  } = profile;

  const handleFormSubmit = async ({
    natFinalsRegion: { value: natFinalsRegion = '' } = {},
    worldSkillsProfession: { value: worldSkillsProfession = '' } = {},
    gender: { value: gender = '' } = {},
    natFinalsRank: { value: natFinalsRank = '' } = {},
    bestWorker: { value: bestWorker = '' } = {},
    bestApprentice: { value: bestApprentice = '' } = {},
    worldSkillsRank: { value: worldSkillsRank = '' } = {},
    euroSkillsRank: { value: euroSkillsRank = '' } = {},
    abilympicsProfession: { label: abilympicsProfession = '' } = {},
    region: { value: region = '' } = {},
    specialJobTitle: { value: specialJobTitle = '' } = {},
    profession: { value: profession = '' } = {},
    associationProfession: { value: associationProfession = '' } = {},
    vocationSchool: { value: vocationSchool = '' } = {},
    swissChampionRank: { value: swissChampionRank = '' } = {},
    participationType: { value: participationType = '' } = {},
    montherLanguage: { value: montherLanguage = '' } = {},
    educationInformation = [],
    ...values
  }) => {
    try {
      let formData = {
        ...values,
        natFinalsRegion,
        worldSkillsProfession,
        gender,
        natFinalsRank,
        bestWorker,
        bestApprentice,
        worldSkillsRank,
        euroSkillsRank,
        abilympicsProfession,
        region,
        specialJobTitle,
        profession,
        associationProfession,
        vocationSchool,
        swissChampionRank,
        participationType,
        montherLanguage,
        regionalEvents: values?.regionalEvents?.map((r) => r.value) || [],
        participantLocation: values?.participantLocation?.value || '',
        educationInformation: educationInformation
      };

      if (formData?.educationInformation?.length > 0) {
        if (!formData?.educationInformation?.some((item) => item?.current)) {
          formData.educationInformation[0].current = true;
        }
      }

      if (formData.functions) formData.functions = formData.functions.map((obj) => obj.value) || [];

      // Remove all values with falsy values
      formData = _.pickBy(formData, (value) => value || value === false);

      await updateProfileData(formData);
      setProfile({ ...formData, isUpdatedForPDF: true, validationReduested: false });
      // toast.success(getTranslation(t, 'form.success', translationPrefix));
    } catch (e) {
      console.log(e);
      toast.error(getTranslation(t, 'form.fail', translationPrefix));
    }
  };

  const getPageData = async () => {
    try {
      const { data: regionData } = await getRegions(profile.masterOrganization);
      const { data: professionData } = await getProfessions(profile.masterOrganization);
      const { data: regionalEventsData } = await getRegionalEvents(
        profile.masterOrganization,
        profile.profileType
      );
      const { data: participantLocationsData } = await getParticipantLocations(
        profile.masterOrganization,
        profile.profileType
      );
      const filteredRegions = getSortedList(regionData, i18n.language, 'name').map((region) => ({
        value: region._id,
        label: region[`name_${i18n.language}`]
      }));

      const filteredProfessions = getSortedList(professionData, i18n.language, 'jobRole').map(
        (profession) => ({
          value: profession._id,
          label: profession[`jobRole_${i18n.language}`]
        })
      );

      setRegions(filteredRegions);
      setProfessions(filteredProfessions);

      // ch champion profile
      const currentLang = i18n.language;
      const { data: swissRegionData } = await getRegions(profile.masterOrganization);
      const { data: professionalAssocData } = await getProfessionalAssociation(
        profile.masterOrganization
      );
      const { data: swissFunctionData } = await getFunction(profile.masterOrganization);
      const { data: swissJobTitleData } = await getSpecialJobTitles(profile.masterOrganization);
      const { data: swissJobChampData } = await getProfessions(profile.masterOrganization);
      const { data: vocationalSchoolData } = await getVocationalSchool(profile.masterOrganization);

      const filteredSwissRegion = swissRegionData.map(({ _id, ...rest }) => ({
        value: _id,
        label: rest[`name_${currentLang}`]
      }));

      const filteredProfessionAssoc = professionalAssocData
        .sort((a, b) => a?.name?.localeCompare(b?.name, currentLang))
        .map(({ name, _id }) => ({
          value: _id,
          label: name
        }));

      const filteredSwissFunction = swissFunctionData.map(({ _id, ...rest }) => ({
        value: _id,
        label: rest[`name_${currentLang}`]
      }));

      const isEN = currentLang === 'en';

      const filteredSwissJobTitle = getSortedList(
        swissJobTitleData,
        isEN ? 'de' : currentLang,
        'name'
      ).map(({ _id, ...rest }) => ({
        value: _id,
        label: rest[`name_${isEN ? 'de' : currentLang}`]
      }));

      const filteredSwissJobChamp = getSortedList(
        swissJobChampData,
        isEN ? 'de' : currentLang,
        'jobRole'
      ).map(({ _id, ...rest }) => ({
        value: _id,
        label: rest[`jobRole_${isEN ? 'de' : currentLang}`]
      }));

      const filteredVocationalSchool = vocationalSchoolData
        .sort((a, b) => a?.name?.localeCompare(b?.name, currentLang))
        .map(({ name, _id }) => ({
          value: _id,
          label: name
        }));

      setSwissJobTitle(swissJobTitleData);
      setListSwissRegions(filteredSwissRegion);
      setListProfessionalAssoc(filteredProfessionAssoc);
      setListSwissFunction(filteredSwissFunction);
      setListSwissJobTitle(filteredSwissJobTitle);
      setProfessions(filteredSwissJobChamp);
      setListVocationalSchool(filteredVocationalSchool);
      setRegionalEvents(
        regionalEventsData?.map((r) => ({ value: r._id, label: r[`name_${currentLang}`] })) || []
      );
      setParticipantLocations(
        participantLocationsData?.map((r) => ({ value: r._id, label: r[`name_${currentLang}`] })) ||
          []
      );
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const getJobsList = async (gender) => {
    let filteredSwissJobTitle = [];
    if (gender === 'masculin') {
      filteredSwissJobTitle = swissJobTitleList.map((item) => ({
        value: item._id,
        label: item.male_de
      }));
    } else {
      filteredSwissJobTitle = swissJobTitleList.map((item) => ({
        value: item._id,
        label: item.female_de
      }));
    }
    setListSwissJobTitle(filteredSwissJobTitle);
  };

  const formSubmitHandler = async (values) => {
    try {
      if (!editableForm) return;

      let missingKeys = [...requiredFieldMissing];

      fieldKeys
        .filter(({ required }) => required)
        .forEach(({ field: { key } = {} }) => {
          if (
            values[key] === undefined ||
            values[key] === null ||
            values[key] === '' ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            if (!requiredFieldMissing.includes(key)) missingKeys.push(key);
          } else {
            missingKeys = missingKeys.filter((item) => item !== key);
          }
        });

      setRequiredFieldMissing([...missingKeys]);

      if (missingKeys.length) {
        setMissingFieldsError(true);
        return;
      }

      setSubmitting(true);
      await handleFormSubmit(values);
      setRequiredFieldMissing([]);
      setMissingFieldsError(false);

      // Navigate to next incomplete step or back to profile
      if (!profile.strengths || profile.strengths.length === 0) {
        navigate('/strengths');
      } else if (!profile.roles || profile.roles.length === 0) {
        navigate('/roles');
      } else {
        navigate('/my-profile');
      }
    } catch (error) {
      console.log(error);
      toast.error(getTranslation(t, `form.error`, translationPrefix));
      setMissingFieldsError(false);
    } finally {
      setSubmitting(false);
      setUnsavedChangesError(false);

      try {
        const errorElement = document?.querySelector('[data-testid="error-text"]');
        if (errorElement)
          setTimeout(
            () =>
              errorElement?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
              }),
            50
          );
      } catch (error) {
        console.log('Error scrolling to error element', error);
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: genericFormSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: formSubmitHandler
  });

  useEffect(() => {
    getPageData();
    getJobsList(profile.gender);
  }, [profile]);

  const handleBack = () => {
    if (formik.dirty) {
      setUnsavedChangesError(true);
    } else {
      navigate('/my-profile');
    }
  };

  return (
    <>
      {/* Scrollable Form Body */}
      <div className="fixed top-24 bottom-20 left-0 right-0 overflow-y-auto">
        <div className="px-6 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Back Button and Heading */}
            <div className="relative mb-2">
              {/* Back Button - Left Aligned */}
              <button
                onClick={handleBack}
                style={{ backgroundColor: colors?.primary || '#2B4B8C' }}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-white p-1.5 rounded-lg hover:opacity-90 transition-opacity">
                <CaretLeftIcon size={16} />
              </button>

              {/* Heading - Centered */}
              <h1
                style={{ color: colors?.primary || '#2B4B8C' }}
                className="text-3xl font-bold text-center"
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'form.title', translationPrefix)
                }}
              />
            </div>
            <p className="text-gray-600 my-8 text-center text-lg max-w-md mx-auto">
              <span
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'form.description', translationPrefix)
                }}
              />
            </p>

            {/* Form Content */}
            <GenericForm
              editableForm={editableForm}
              profile={profile}
              fieldKeys={fieldKeys}
              regions={regions}
              professions={professions}
              listSwissRegions={listSwissRegions}
              listProfessionalAssoc={listProfessionalAssoc}
              listSwissFunction={listSwissFunction}
              listSwissJobTitle={listSwissJobTitle}
              listVocationalSchool={listVocationalSchool}
              regionalEvents={regionalEvents}
              participantLocations={participantLocations}
              establishmentsListForProfileForm={customData?.establishmentsListForProfileForm}
              handleFormSubmit={handleFormSubmit}
              orgPrefix={translationPrefix}
              formik={formik}
              initialValues={initialValues}
              setInitialValues={setInitialValues}
              requiredFieldMissing={requiredFieldMissing}
              submitting={submitting}
              hideSubmitButton={true}
            />
          </div>
        </div>
      </div>

      {/* Fixed Footer with Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 px-6 py-6 z-40">
        <div className="max-w-2xl mx-auto flex justify-center">
          <Button.ButtonRounded
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
            bgColor="secondary"
            className="w-full max-w-xs py-3">
            <Spinner isSubmitting={submitting} />
            {getTranslation(t, 'form.submit', translationPrefix)}
          </Button.ButtonRounded>
        </div>
      </div>

      {/* Loaders and Popups */}
      {loading && <Loader transparent={true} />}

      {missingFieldsError && (
        <Popups.FieldsMissing
          close={() => setMissingFieldsError(false)}
          orgPrefix={translationPrefix}
        />
      )}

      {unsavedChangesError && (
        <Popups.UnsavedChanges
          close={() => {
            setUnsavedChangesError(false);
            navigate('/my-profile');
          }}
          orgPrefix={translationPrefix}
          saveCallback={() => formik.handleSubmit()}
        />
      )}
    </>
  );
}
